import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const HEARTBEAT_MS = 15000;

/**
 * VisitorTracker Component
 *
 * Tracks visitor sessions and notifies the backend when a new visitor enters,
 * keeps them "live" with periodic heartbeats + live page updates on every route
 * change, and tells the backend the instant they leave (so admin's Live Visitors
 * list reflects reality, not minutes/hours-old sessions).
 */
const VisitorTracker = () => {
  const location = useLocation();
  const sessionIdRef = useRef(null);
  const isAdminRef = useRef(false);

  useEffect(() => {
    // Don't track the site owner's own browsing as a "visitor"
    const isAdmin = localStorage.getItem('admin_token') || localStorage.getItem('admin_key');
    isAdminRef.current = !!isAdmin;
    if (isAdmin) return;

    // Don't count admin-area pages (login screen, dashboards, etc.) as visitor activity
    if (window.location.pathname.startsWith('/admin')) return;

    // Don't count testing/preview traffic — only the live production domain counts as a real visitor
    const host = window.location.hostname;
    const isPreviewOrLocal = host.includes('preview.emergentagent.com') || host.includes('localhost') || host === '127.0.0.1';
    if (isPreviewOrLocal) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const trackVisitor = async () => {
      try {
        let sessionId = sessionStorage.getItem('visitor_session_id');
        if (!sessionId) {
          // Persistent (localStorage) id — survives across sessions/tabs so the
          // backend can tell first-timers apart from people who've visited before.
          let visitorUid = localStorage.getItem('visitor_uid');
          if (!visitorUid) {
            visitorUid = crypto.randomUUID ? crypto.randomUUID() : `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
            localStorage.setItem('visitor_uid', visitorUid);
          }

          const response = await axios.post(`${backendUrl}/api/visitor/ping`, null, {
            params: { current_page: window.location.pathname, visitor_uid: visitorUid }
          });
          if (response.data.success) {
            sessionId = response.data.session_id;
            sessionStorage.setItem('visitor_session_id', sessionId);
            sessionStorage.setItem('visitor_tracked', 'true');
            console.log('[VISITOR] Session tracked:', sessionId, 'Page:', window.location.pathname);
          }
        }
        sessionIdRef.current = sessionId || null;
      } catch (error) {
        // Silent fail - don't interrupt user experience
        console.log('[VISITOR] Tracking skipped:', error.message);
      }
    };

    // Track after a short delay to not block initial render
    const timer = setTimeout(trackVisitor, 1000);

    // Heartbeat keeps this visitor showing as truly "live" (updates last_seen + current page)
    const heartbeat = setInterval(() => {
      if (!sessionIdRef.current) return;
      axios.post(`${backendUrl}/api/visitor/heartbeat`, {
        session_id: sessionIdRef.current,
        current_page: window.location.pathname
      }).catch(() => {});
    }, HEARTBEAT_MS);

    // Tell the backend the moment they actually leave, so they vanish from
    // "Live Visitors" instantly instead of lingering until a timeout.
    const sendLeaveBeacon = () => {
      if (!sessionIdRef.current || !navigator.sendBeacon) return;
      const blob = new Blob([JSON.stringify({ session_id: sessionIdRef.current })], { type: 'application/json' });
      navigator.sendBeacon(`${backendUrl}/api/visitor/leave`, blob);
    };
    window.addEventListener('pagehide', sendLeaveBeacon);

    return () => {
      clearTimeout(timer);
      clearInterval(heartbeat);
      window.removeEventListener('pagehide', sendLeaveBeacon);
    };
  }, []);

  // Fire an immediate heartbeat with the new page whenever the SPA route changes,
  // so "Current Page" in the admin dashboard updates live as they browse.
  useEffect(() => {
    if (isAdminRef.current || !sessionIdRef.current || location.pathname.startsWith('/admin')) return;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    axios.post(`${backendUrl}/api/visitor/heartbeat`, {
      session_id: sessionIdRef.current,
      current_page: location.pathname
    }).catch(() => {});
  }, [location.pathname]);

  // This component renders nothing
  return null;
};

export default VisitorTracker;
